/**
 * React Query hooks for Contacts & Favorites
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { contactsService } from '@/api/services/contacts.service';
import { message } from 'antd';
import { useAuth } from '@/context/AuthContext';

export const CONTACTS_QUERY_KEY = 'contacts';
export const FAVORITES_QUERY_KEY = 'favorites';

/**
 * Hook for fetching all contacts
 */
export const useContacts = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: [CONTACTS_QUERY_KEY],
    queryFn: () => contactsService.getAllContacts(user?.id || ''),
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook for fetching favorite contacts
 */
export const useFavorites = () => {
  return useQuery({
    queryKey: [FAVORITES_QUERY_KEY],
    queryFn: () => contactsService.getFavorites(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook for adding a contact to favorites
 */
export const useAddToFavorites = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) => contactsService.addToFavorites(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [FAVORITES_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [CONTACTS_QUERY_KEY] });
      message.success('Added to favorites');
    },
    onError: (error: Error) => {
      message.error(error?.message || 'Failed to add to favorites');
    },
  });
};

/**
 * Hook for removing a contact from favorites
 */
export const useRemoveFromFavorites = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) => contactsService.removeFromFavorites(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [FAVORITES_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [CONTACTS_QUERY_KEY] });
      message.success('Removed from favorites');
    },
    onError: (error: Error) => {
      message.error(error?.message || 'Failed to remove from favorites');
    },
  });
};
